import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from './Button';
import ScreenTransition from './ScreenTransition';

function PrivacyPolicy() {
  const navigate = useNavigate();
  return (
    <ScreenTransition direction="right">
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <div className="bg-zinc-900/60 px-4 py-6 sm:p-8 rounded-2xl max-w-4xl w-full overflow-y-auto max-h-[90vh]">
          <div className="mb-6 flex flex-col items-center">
            <Button onClick={() => navigate('/')} className="w-32 mb-6">
              Back
            </Button>
            <h1 className="text-2xl sm:text-3xl font-bold text-center mb-6 text-yellow-300">
              Privacy Policy
            </h1>
            <p className="text-gray-400 text-sm text-center mb-8">
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>

          <div className="space-y-6 text-gray-200 text-sm sm:text-base leading-relaxed">
            <section>
              <h2 className="text-xl font-semibold text-yellow-300 mb-3">Introduction</h2>
              <p className="mb-3">
                Welcome to Wall Go. This Privacy Policy explains how we handle information when you use our website and game application. We are committed to protecting your privacy and ensuring transparency about our data practices.
              </p>
              <p>
                Wall Go is a browser-based game that operates entirely client-side. We have designed our application to minimize data collection and respect user privacy while providing an enjoyable gaming experience.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-yellow-300 mb-3">Information We Collect</h2>
              <p className="mb-3">
                Wall Go is designed with privacy in mind. We do not collect, store, or transmit any personal information from users. The application operates entirely within your web browser and does not require:
              </p>
              <ul className="list-disc list-inside mb-3 space-y-2 ml-4">
                <li>User registration or account creation</li>
                <li>Login credentials or authentication</li>
                <li>Personal data submission (name, email, address, etc.)</li>
                <li>Payment information</li>
                <li>Location data</li>
                <li>Device identifiers</li>
              </ul>
              <p>
                All game data, including game state, moves, and preferences, are stored locally in your browser's memory and are not transmitted to our servers. When you close your browser or navigate away from the page, this data is cleared.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-yellow-300 mb-3">Cookies and Tracking Technologies</h2>
              <p className="mb-3">
                We do not use cookies, web beacons, pixel tags, or any other tracking technologies on our website. Our game and website functionality operate without requiring any form of tracking or persistent storage.
              </p>
              <p>
                Your web browser may store certain technical information (such as cached files) as part of normal browser operation, but this is standard browser behavior and not something we actively collect or control.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-yellow-300 mb-3">Third-Party Services</h2>
              <p className="mb-3">
                Wall Go is a standalone application that does not integrate with third-party analytics services, advertising networks, or social media platforms. We do not share any user information with third parties because we do not collect any information to share.
              </p>
              <p>
                If you access Wall Go through a third-party website or platform, that platform may have its own privacy policies and data collection practices. We encourage you to review those policies separately.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-yellow-300 mb-3">Data Security</h2>
              <p className="mb-3">
                Since we do not collect or store any personal information, there is no user data at risk of unauthorized access, disclosure, or breach. The game operates entirely within your browser's secure environment.
              </p>
              <p>
                We recommend that users keep their web browsers updated to the latest version to ensure they have the most recent security patches and protections.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-yellow-300 mb-3">Children's Privacy</h2>
              <p className="mb-3">
                Wall Go is designed to be safe for users of all ages, including children. Since we do not collect any personal information, we comply with children's privacy protection requirements by default.
              </p>
              <p>
                Parents and guardians can be assured that their children can enjoy Wall Go without any risk of personal information being collected or shared.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-yellow-300 mb-3">Your Rights</h2>
              <p className="mb-3">
                Since we do not collect personal information, there is no personal data to access, modify, or delete. However, you have the right to:
              </p>
              <ul className="list-disc list-inside mb-3 space-y-2 ml-4">
                <li>Use Wall Go without providing any personal information</li>
                <li>Clear your browser's cache and local storage at any time</li>
                <li>Disable JavaScript if you prefer (though this will prevent the game from functioning)</li>
                <li>Contact us with any privacy-related questions or concerns</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-yellow-300 mb-3">International Users</h2>
              <p>
                Wall Go is accessible to users worldwide. Since we do not collect personal information, our privacy practices are consistent regardless of your location. We do not transfer any data across international borders because we do not collect any data to transfer.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-yellow-300 mb-3">Changes to This Privacy Policy</h2>
              <p className="mb-3">
                We may update this Privacy Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. Any changes will be posted on this page with an updated "Last updated" date.
              </p>
              <p>
                We encourage you to review this Privacy Policy periodically to stay informed about how we protect your privacy. Your continued use of Wall Go after any changes to this policy constitutes acceptance of those changes.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-yellow-300 mb-3">Contact Us</h2>
              <p className="mb-3">
                If you have any questions, concerns, or requests regarding this Privacy Policy or our privacy practices, please contact us at:
              </p>
              <p>
                <a
                  href="mailto:tete.dans.les.clouds@gmail.com"
                  className="text-yellow-300 hover:text-yellow-400 underline transition-colors"
                >
                  tete.dans.les.clouds@gmail.com
                </a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </ScreenTransition>
  );
}

export default PrivacyPolicy;
